/**
 * Backgammon の VRF (Verifiable Random Function) 流れを Play.tsx から切り出すフック。
 *
 * 仕様 (DO 経路 / ローカル CPU 共通):
 *   1. wasm 初期化済 ∧ 自分が観戦者でない場合のみ動作
 *   2. 各 Backgammon ボードについて: 自分の公開鍵を server に publish (再送防止は useRoom 内)
 *   3. **真のターン開始時のみ** (= `dice=[]` ∧ `turn_history=[]`) proof を提出
 *      DO は両者の proof を combine して seed を作り、 dice を broadcast する
 *
 * **重要**: 「ターン途中で dice 全消費した」 場合 (= EndTurn 待ち) は dice=[] でも proof を送らない。
 *   送ると DO が新ラウンドの dice を同一 turn にもう一度 inject してしまい、
 *   ・state.round が予期せず増える
 *   ・dice 配列に余計な値が混入
 *   ・auto-EndTurn の eligible 条件が「legal が [EndTurn] のみ」 から崩れて永遠に発火しない
 *   というバグ連鎖になる。 `turn_history` 非空 ⇔ 自分が 1 ply 以上指した、 で判別する
 *   ([[feedback_spec_first]] BG VRF gate と整合)。
 *
 * Play.tsx は値を返さず副作用のみ。 ローカル CPU 対戦では VRF は未対応で、
 * local-connection が「dice 空 → ローカル乱数で振り直し」 する (= ここの送信は no-op)。
 */

import { useEffect } from 'react';
import type { BoardEntry } from '../lib/match-snapshot';
import { useRoom } from '../state/room';

export function useBackgammonVrf({
  boards,
  you,
  wasmReady,
}: {
  boards: BoardEntry[];
  you: 'host' | 'guest' | 'spectator' | null;
  wasmReady: boolean;
}): void {
  const ensureVrfPublished = useRoom((s) => s.ensureVrfPublished);
  const submitVrfProof = useRoom((s) => s.submitVrfProof);
  const matchCode = useRoom((s) => s.code ?? '');
  const vrfFingerprint = useRoom(state => [...state.vrfContexts].map(([board, context]) => `${board}:${context}:${state.vrf.get(board)?.hostPublicKey ? 1 : 0}:${state.vrf.get(board)?.guestPublicKey ? 1 : 0}`).join('|'));
  // biome-ignore lint/correctness/useExhaustiveDependencies: context and key acknowledgements resume the protocol
  useEffect(() => {
    const run = () => {
    if (!wasmReady || !you || you === 'spectator') return;
    for (const b of boards) {
      if (b.game_id !== 'Backgammon' || b.result !== null) continue;
      try {
        ensureVrfPublished(b.board);
        const bgState = b.state as {
          dice?: number[];
          round?: number;
          turn_history?: unknown[];
        };
        const diceEmpty = Array.isArray(bgState?.dice) && bgState.dice.length === 0;
        const historyEmpty =
          !Array.isArray(bgState?.turn_history) || bgState.turn_history.length === 0;
        if (diceEmpty && historyEmpty) {
          const round = bgState.round ?? 0;
          submitVrfProof(b.board, round, matchCode);
        }
      } catch (e) {
        // VRF 失敗で Play 全体を固めないように呑む。 サーバ側が再送を期待。
        console.error('VRF flow error', e);
      }
    }
    };
    run();
    const retry = setInterval(run, 2500);
    return () => clearInterval(retry);
  }, [wasmReady, boards, you, ensureVrfPublished, submitVrfProof, matchCode, vrfFingerprint]);
}
